import { useSnackbar } from '@robocorp/components';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch } from '~/hooks';
import { webhooksService } from '~/services/webhooksAPI';
import {
  CreateProcessWebhookInput,
  CreateWebhookInput,
  EditWebhookInput,
  ListWebhookLogsQueryParams,
} from '~/services/webhooksAPI/webhooksService';
import {
  QueryError,
  UseApiMutationOptions,
  UseApiQueryOptions,
  formatErrorPayload,
  validateApiResponse,
} from '../utils';
import { useCurrentWorkspaceQueryKey } from '../utils/useQueryKey';

export type UseWebhookLogsQueryVariables = {
  webhookId: string;
  size?: number;
  filters?: {
    showOnlyErrors?: true | false;
    eventType?: string | null;
  };
};

const ROOT_KEY = ['webhooks'];
const getProcessWebhooksQueryKey = (processId: string) => [...ROOT_KEY, processId];
const getWebhookQueryKey = (webhookId: string) => [...ROOT_KEY, webhookId];
const getWebhookLogsQueryKey = (variables: UseWebhookLogsQueryVariables) => [
  ...ROOT_KEY,
  variables.webhookId,
  variables.filters?.showOnlyErrors,
  variables.filters?.eventType,
  'logs',
];
const getWebhookLogsByProcessQueryKey = (processId: string, processRunId: string) => [
  ...ROOT_KEY,
  processId,
  processRunId,
  'logs',
];

export const useWebhooksQuery = (
  options?: UseApiQueryOptions<typeof webhooksService.listWebhooks>,
) => {
  const dispatch = useDispatch();

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useQuery(
    queryKeyFormatter(ROOT_KEY),
    async () => validateApiResponse(await webhooksService.listWebhooks({ workspaceId })).data,
    {
      refetchOnMount: true,
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useProcessWebhooksQuery = (
  variables: { processId: string },
  options?: UseApiQueryOptions<typeof webhooksService.listProcessWebhooks>,
) => {
  const dispatch = useDispatch();

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useQuery(
    queryKeyFormatter(getProcessWebhooksQueryKey(variables.processId)),
    async () =>
      validateApiResponse(
        await webhooksService.listProcessWebhooks({ workspaceId, processId: variables.processId }),
      ).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useWebhookQuery = (
  variables: { webhookId: string },
  options?: UseApiQueryOptions<typeof webhooksService.getWebhook>,
) => {
  const dispatch = useDispatch();

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useQuery(
    queryKeyFormatter(getWebhookQueryKey(variables.webhookId)),
    async () =>
      validateApiResponse(
        await webhooksService.getWebhook({ workspaceId, webhookId: variables.webhookId }),
      ).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useCreateWebhookMutation = (
  options?: UseApiMutationOptions<typeof webhooksService.createWebhook>,
) => {
  const { t } = useTranslation();
  const { addSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (variables: CreateWebhookInput) => {
      const response = validateApiResponse(
        await webhooksService.createWebhook({ workspaceId, params: variables }),
      );
      return response.data;
    },
    {
      onError: (error: Error) => {
        dispatch(formatErrorPayload(error));
      },
      onSuccess: () => {
        addSnackbar({ message: t('processWebhooks.webhookAdded'), variant: 'success' });
      },
      ...options,
    },
  );
};

export type UseCreateProcessWebhookVariables = CreateProcessWebhookInput & { processId: string };

export const useCreateProcessWebhookMutation = (
  options?: UseApiMutationOptions<typeof webhooksService.createProcessWebhook>,
) => {
  const { t } = useTranslation();
  const { addSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId, ...variables }: UseCreateProcessWebhookVariables) => {
      const response = validateApiResponse(
        await webhooksService.createProcessWebhook({ workspaceId, processId, params: variables }),
      );
      return response.data;
    },
    {
      onError: (error: Error) => {
        dispatch(formatErrorPayload(error));
      },
      onSuccess: () => {
        addSnackbar({ message: t('processWebhooks.webhookAdded'), variant: 'success' });
      },
      ...options,
    },
  );
};

export const useDeleteWebhookMutation = (
  options?: UseApiMutationOptions<typeof webhooksService.deleteWebhook>,
) => {
  const { t } = useTranslation();
  const { addSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (variables: { webhookId: string }) => {
      const response = validateApiResponse(
        await webhooksService.deleteWebhook({ workspaceId, webhookId: variables.webhookId }),
      );
      return response.data;
    },
    {
      onError: (error: Error) => {
        dispatch(formatErrorPayload(error));
      },
      onSuccess: () => {
        addSnackbar({
          message: t('webhooks.deleteSuccess'),
          variant: 'success',
        });
      },
      ...options,
    },
  );
};

export const useSendTestEventMutation = (
  options?: UseApiMutationOptions<typeof webhooksService.sendTestEvent>,
) => {
  const { t } = useTranslation();
  const { addSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (variables: { webhookId: string }) => {
      const response = validateApiResponse(
        await webhooksService.sendTestEvent({ workspaceId, webhookId: variables.webhookId }),
      );
      return response.data;
    },
    {
      onError: (error: Error) => {
        dispatch(formatErrorPayload(error));
      },
      onSuccess: () => {
        addSnackbar({
          message: t('webhooks.testSuccess'),
          variant: 'success',
        });
      },
      ...options,
    },
  );
};

export const useEditWebhookMutation = (
  options?: UseApiMutationOptions<typeof webhooksService.editWebhook>,
) => {
  const { t } = useTranslation();
  const { addSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ webhookId, params }: { webhookId: string; params: EditWebhookInput }) => {
      const response = validateApiResponse(
        await webhooksService.editWebhook({ workspaceId, webhookId, params }),
      );
      return response.data;
    },
    {
      onError: (error: Error) => {
        dispatch(formatErrorPayload(error));
      },
      onSuccess: () => {
        addSnackbar({
          message: t('webhooks.editSuccess'),
          variant: 'success',
        });
      },
      ...options,
    },
  );
};

export const useWebhookLogsQuery = (variables: UseWebhookLogsQueryVariables) => {
  const dispatch = useDispatch();

  const { webhookId, size = 50, filters } = variables;

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useInfiniteQuery({
    queryKey: queryKeyFormatter(getWebhookLogsQueryKey(variables)),
    queryFn: async ({ pageParam }) => {
      return validateApiResponse(
        await webhooksService.listWebhookLogs({
          workspaceId,
          webhookId,
          params: pageParam ?? {
            size,
            cursor: null,
            showOnlyErrors: filters?.showOnlyErrors,
            eventType: filters?.eventType,
          },
        }),
      ).data;
    },
    getNextPageParam: (lastPage) => {
      const { nextCursor = null } = (lastPage as { nextCursor: string | null } | undefined) ?? {};
      return {
        cursor: nextCursor,
        size,
        showOnlyErrors: filters?.showOnlyErrors,
        eventType: filters?.eventType,
      } as ListWebhookLogsQueryParams;
    },
    onError: (error: Error | QueryError) => dispatch(formatErrorPayload(error)),
  });
};

export const useWebhookLogsByProcessRunQuery = (
  processId: string,
  processRunId: string,
  size = 50,
) => {
  const dispatch = useDispatch();

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useInfiniteQuery({
    queryKey: queryKeyFormatter(getWebhookLogsByProcessQueryKey(processId, processRunId)),
    queryFn: async ({ pageParam }) => {
      return validateApiResponse(
        await webhooksService.listWebhookLogsByProcessRun({
          workspaceId,
          pathParams: { processId, processRunId },
          params: pageParam ?? { size, cursor: null },
        }),
      ).data;
    },
    getNextPageParam: (lastPage) => {
      const { nextCursor = null } = (lastPage as { nextCursor: string | null } | undefined) ?? {};
      return {
        cursor: nextCursor,
        size,
      } as ListWebhookLogsQueryParams;
    },
    onError: (error: Error | QueryError) => dispatch(formatErrorPayload(error)),
  });
};
